import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { t } from "../i18n";
import { DeliveryMap } from "../components/DeliveryMap";
import { StarRating } from "../components/StarRating";
import { MapPin, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function DeliverLocation() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [state, setState] = useState("loading"); // loading | invalid | ready | done
  const [locale, setLocale] = useState("fr");
  const [mapsKey, setMapsKey] = useState("");
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rated, setRated] = useState(false);

  useEffect(() => {
    api.get("/bootstrap").then((r) => setMapsKey(r.data?.maps?.google_maps_api_key || "")).catch(() => {});
    api.get(`/geo/location-requests/${token}`)
      .then((r) => {
        setReq(r.data);
        setLocale(r.data.locale || "fr");
        if (r.data.location) { setLoc(r.data.location); setState("done"); }
        else setState("ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = (pos, source) => {
    setBusy(true);
    setErr("");
    api.post(`/geo/location-requests/${token}/submit`, {
      lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy || null, source: source || "device",
    })
      .then(() => { setLoc(pos); setState("done"); })
      .catch(() => setErr("error"))
      .finally(() => setBusy(false));
  };

  const share = () => {
    if (!navigator.geolocation) { setErr(t(locale, "enableLocation")); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => submit({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }, "device"),
      () => { setBusy(false); setErr(t(locale, "enableLocation")); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const dir = locale === "ar" ? "rtl" : "ltr";

  const rate = (stars) => {
    api.post(`/geo/location-requests/${token}/rate`, { stars }).then(() => setRated(true)).catch(() => setRated(true));
  };
  const showRating = req && (req.can_rate || rated);

  return (
    <div dir={dir} className="min-h-screen bg-plaster flex flex-col items-center px-5 py-10" data-testid="deliver-page">
      <div className="font-display text-2xl text-ink tracking-tight mb-8">Fleurinelle</div>
      <div className="w-full max-w-md card p-6 space-y-5">
        {state === "loading" ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-brass" /></div>
        ) : null}

        {state === "invalid" ? (
          <div className="text-center space-y-3" data-testid="deliver-invalid">
            <AlertCircle className="mx-auto text-danger" size={28} />
            <p className="text-ink">{t(locale, "deliverExpired")}</p>
          </div>
        ) : null}

        {state === "ready" ? (
          <div className="space-y-5" data-testid="deliver-ready">
            <div className="flex items-center gap-2">
              <img src="/rose-pin.png" alt="" className="w-8 h-8" />
              <h1 className="font-display text-2xl text-ink">{t(locale, "deliverTitle")}</h1>
            </div>
            <p className="text-sm text-muted">
              {req?.recipient_name ? `${req.recipient_name} — ` : ""}{t(locale, "deliverIntro")}
            </p>
            <button data-testid="deliver-share" className="btn-primary w-full flex items-center justify-center gap-2"
                    disabled={busy} onClick={share}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
              {t(locale, "shareMyLocation")}
            </button>
            {mapsKey ? (
              <DeliveryMap apiKey={mapsKey} locale={locale} autocomplete={false} testid="deliver-map"
                           value={loc} onChange={(p) => submit(p, "pin")} />
            ) : null}
            {err ? <p className="text-danger text-sm" data-testid="deliver-error">{err}</p> : null}
          </div>
        ) : null}

        {state === "done" ? (
          <div className="space-y-5" data-testid="deliver-done">
            <div className="text-center space-y-2">
              <CheckCircle2 className="mx-auto text-brass" size={32} />
              <p className="text-ink font-medium">{t(locale, "deliverThanks")}</p>
            </div>
            {mapsKey ? (
              <>
                <DeliveryMap apiKey={mapsKey} locale={locale} autocomplete={false} testid="deliver-map-done"
                             value={loc} onChange={(p) => submit(p, "pin")} />
                <p className="text-xs text-muted text-center">{t(locale, "deliverAdjust")}</p>
              </>
            ) : null}
          </div>
        ) : null}

        {showRating ? (
          <div className="border-t border-brass/20 pt-4 text-center space-y-3" data-testid="deliver-rate">
            <p className="text-sm text-ink">{req.driver_name ? t(locale, "rateDriver").replace("{name}", req.driver_name) : t(locale, "rateDelivery")}</p>
            {rated ? <p className="text-sm text-brass" data-testid="deliver-rated">{t(locale, "feedbackThanks")} ★</p> : (
              <div className="flex justify-center"><StarRating onRate={rate} testid="deliver-stars" /></div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
